import { useCurrentUserResource } from '@dispatch/shared/hooks';
import { MultiSelectOption, TaskResponse } from '@dispatch/shared/types';
import {
  CheckBoxField,
  DateTimeField,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import { Grid } from '@mui/material';
import React from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
} from 'react-hook-form';

export const TimingSection = ({
  control,
  errors,
  hasUpsertPermissions,
  hasAppointment,
  setValue,
  taskData,
  rescheduleReasons,
  isDirty,
}: {
  errors: FieldErrors;
  control: Control<FieldValues>;
  hasUpsertPermissions: unknown;
  hasAppointment: boolean;
  setValue?: UseFormSetValue<FieldValues>;
  taskData?: TaskResponse;
  rescheduleReasons?: MultiSelectOption[];
  isDirty: boolean;
}) => {
  const { isAdjuster } = useCurrentUserResource();

  const [needsReason, setNeedsReason] = React.useState(false);

  React.useEffect(() => {
    setValue?.('reasonCode', '');
    if (isDirty) {
      // used to update validation to show that reason is required if it is shown
      setValue?.('needsReason', true);
      // used to update state to show the reason drop down
      setNeedsReason(true);
    } else {
      setValue?.('needsReason', false);
      setNeedsReason(false);
    }
  }, [isDirty]);

  const handleDateCalculation = (field: string, value: string) => {
    const duration =
      (taskData?.$msg?.assignmentHandling?.durationInMinutes || 0) *
      (1 / (taskData?.$metadata?.appliedEfficiencyCoefficient || 1));

    const date = new Date(value);
    date.setMinutes(Number(date.getMinutes()) + duration);
    setValue?.(`assignmentHandling.${field}`, date);
  };

  const renderReasonCodeField = () => {
    return (
      <Grid item xs={6}>
        <SelectField
          id={'reasonCode'}
          name={'reasonCode'}
          label={'Reschedule Reason'}
          control={control}
          errors={errors}
          options={rescheduleReasons || []}
          required
        />
      </Grid>
    );
  };

  return (
    <>
      <Grid container spacing={2} alignItems="end" data-testid="loss-time-zone">
        <Grid item xs={3}>
          <TextField
            id="claim.timeZone"
            name="claim.timeZone"
            label="Loss Time Zone"
            required
            control={control}
            errors={errors}
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            id="assignmentHandling.durationInMinutes"
            name="assignmentHandling.durationInMinutes"
            label="Expected Duration (Minutes)"
            inputProps={{ disabled: true }}
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid item xs={3}>
          <DateTimeField
            id="appointmentStart"
            name="assignmentHandling.appointmentStart"
            label="Appointment Start"
            control={control}
            onChange={(value) => handleDateCalculation('appointmentEnd', value)}
            errors={
              errors && errors['assignmentHandling']
                ? errors['assignmentHandling']
                : null
            }
            readOnly={!hasUpsertPermissions || !!isAdjuster}
          />
        </Grid>
        <Grid item xs={3}>
          <DateTimeField
            id="assignmentHandling.appointmentEnd"
            name="assignmentHandling.appointmentEnd"
            label="Appointment End"
            control={control}
            readOnly={true}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} marginBottom={2} alignItems="center">
        <Grid item xs={6}>
          <CheckBoxField
            control={control}
            id="assignmentHandling.travel"
            name="assignmentHandling.travel"
            label="Travel Required"
            disabled
          />
        </Grid>
        {!hasAppointment ? (
          <>
            {/*Show if there is no reason. This is needed for an offset. Offets are supported in mui Grid v2, so when we update we can remove this */}
            <Grid item xs={3}>
              <DateTimeField
                id="assignmentHandling.mustStartAfterTime"
                name="assignmentHandling.mustStartAfterTime"
                label="Projected Start Time"
                control={control}
                errors={errors}
                readOnly={true}
              />
            </Grid>
            <Grid item xs={3}>
              <DateTimeField
                id="assignmentHandling.mustEndBeforeTime"
                name="assignmentHandling.mustEndBeforeTime"
                label="Projected End Time"
                control={control}
                errors={errors}
                readOnly={true}
              />
            </Grid>
            <Grid item xs={6}></Grid>
            {renderReasonCodeField()}
          </>
        ) : (
          needsReason === true && renderReasonCodeField()
        )}
      </Grid>
    </>
  );
};
